import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  Plus,
  Trash2,
  Bot,
  User as UserIcon,
  ExternalLink,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  listKnowledge,
  archiveKnowledge,
  unarchiveKnowledge,
  deleteKnowledge,
  updateKnowledge,
  createKnowledge,
} from "@/api/knowledge";
import type {
  KnowledgeBaseEntry,
  KnowledgeCategory,
  KnowledgeBaseCreate,
} from "@/types";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/dates";

type CategoryKey = "all" | KnowledgeCategory;

interface CategoryMeta {
  id: CategoryKey;
  label: string;
  hint?: string;
}

const CATEGORIES: CategoryMeta[] = [
  { id: "all", label: "knowledge.cat_all" },
  { id: "equipment", label: "knowledge.cat_equipment" },
  { id: "exercises", label: "knowledge.cat_exercises" },
  { id: "nutrition", label: "knowledge.cat_nutrition" },
  { id: "training_insights", label: "knowledge.cat_training" },
  { id: "medical_recovery", label: "knowledge.cat_health" },
  { id: "race_strategy", label: "knowledge.cat_strategy" },
  { id: "general", label: "knowledge.cat_general" },
];

// CATEGORY_LABEL is a key map — t() it at usage site.
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORIES.filter((c) => c.id !== "all").map((c) => [c.id, c.label]),
);

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const [category, setCategory] = useState<CategoryKey>("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["knowledge", category, search, showArchived],
    queryFn: () =>
      listKnowledge({
        category: category === "all" ? undefined : category,
        search: search.trim() || undefined,
        show_archived: showArchived,
      }),
    staleTime: 10_000,
  });

  const archiveM = useMutation({
    mutationFn: (id: number) => archiveKnowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });
  const unarchiveM = useMutation({
    mutationFn: (id: number) => unarchiveKnowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => deleteKnowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });
  const pinM = useMutation({
    mutationFn: (args: { id: number; pinned: boolean }) =>
      updateKnowledge(args.id, { pinned: args.pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });

  const entries = listQ.data?.entries ?? [];
  const counts = listQ.data?.categories ?? {};
  const totalAll = useMemo(
    () => Object.values(counts).reduce((s, n) => s + n, 0),
    [counts],
  );

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <SectionLabel>{t("knowledge.section_title")}</SectionLabel>
          <h1 className="font-serif text-[26px] font-medium leading-snug md:text-[30px]">
            {t("knowledge.h1")}
          </h1>
          <p className="mt-1 text-[13px] text-taupe">
            {t("knowledge.subtitle")}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          {t("knowledge.add_entry").replace(/^[+]\s*/,"")}
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe"
          strokeWidth={1.8}
        />
        <input
          type="text"
          placeholder={t("knowledge.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-cream-border bg-cream-light py-3 pl-10 pr-4 text-[14px] text-navy-deep placeholder:text-taupe focus:border-amber/60 focus:outline-none"
        />
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATEGORIES.map((cat) => {
          const count = cat.id === "all" ? totalAll : counts[cat.id] ?? 0;
          const isActive = category === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors",
                isActive
                  ? "border-navy bg-navy text-cream"
                  : "border-cream-border bg-cream-light text-navy-deep hover:bg-cream-border/40",
              )}
            >
              {t(cat.label)}
              <span className={cn("ml-1.5 text-[11px]", isActive ? "text-cream-light" : "text-taupe")}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <label className="inline-flex items-center gap-2 text-[12px] text-taupe cursor-pointer select-none">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-cream-border"
        />
        Показать архивные
      </label>

      {/* Entries */}
      {listQ.isLoading ? (
        <div className="py-10 text-center text-taupe">Загрузка…</div>
      ) : entries.length === 0 ? (
        <Card>
          <div className="py-6 text-center">
            <p className="mb-1 text-[14px] font-medium text-navy-deep">Пока пусто</p>
            <p className="mx-auto max-w-md text-[13px] text-taupe">
              Записи появляются автоматически когда ты обсуждаешь с тренером что-то важное.
              Либо добавь вручную кнопкой сверху.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              expanded={expandedId === entry.id}
              onToggle={() =>
                setExpandedId(expandedId === entry.id ? null : entry.id)
              }
              onPin={() => pinM.mutate({ id: entry.id, pinned: !entry.pinned })}
              onArchive={() =>
                entry.archived ? unarchiveM.mutate(entry.id) : archiveM.mutate(entry.id)
              }
              onDelete={() => {
                if (confirm("Удалить запись навсегда?")) deleteM.mutate(entry.id);
              }}
            />
          ))}
        </div>
      )}

      {creating ? (
        <CreateModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["knowledge"] });
          }}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// EntryCard
// ============================================================================

function EntryCard({
  entry,
  expanded,
  onToggle,
  onPin,
  onArchive,
  onDelete,
}: {
  entry: KnowledgeBaseEntry;
  expanded: boolean;
  onToggle: () => void;
  onPin: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const catLabel = CATEGORY_LABEL[entry.category] || entry.category;
  const isAuto = entry.source === "chat_auto";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border bg-cream-light shadow-soft transition-colors",
        entry.archived
          ? "border-cream-border opacity-60"
          : entry.pinned
            ? "border-amber/40"
            : "border-cream-border",
      )}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-start gap-3 p-4 text-left hover:bg-cream-border/20"
      >
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/15 text-amber-dark">
          {isAuto ? <Bot className="h-4 w-4" strokeWidth={1.8} /> : <UserIcon className="h-4 w-4" strokeWidth={1.8} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            {entry.pinned ? <Pin className="h-3 w-3 text-amber-dark" strokeWidth={2} /> : null}
            <span className="text-[10px] font-medium uppercase tracking-[1.5px] text-amber-dark">
              {catLabel}
            </span>
            <span className="text-[10px] text-taupe">· {fmtDate(entry.created_at)}</span>
            {entry.reference_count > 0 ? (
              <span className="text-[10px] text-taupe">· ref ×{entry.reference_count}</span>
            ) : null}
          </div>
          <h3 className="font-medium text-[15px] text-navy-deep">{entry.title}</h3>
          {entry.tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {entry.tags.slice(0, 6).map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-amber/10 px-1.5 py-0.5 text-[10px] text-amber-dark"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </button>

      {expanded ? (
        <div className="border-t border-cream-border bg-cream px-4 py-4">
          <div className="coach-markdown prose-sm max-w-none text-[14px] text-navy-deep">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.content}</ReactMarkdown>
          </div>

          {entry.external_links.length > 0 ? (
            <div className="mt-4 border-t border-cream-border pt-3">
              <div className="section-label mb-2">Ссылки</div>
              <ul className="space-y-1">
                {entry.external_links.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-[13px] text-amber-dark hover:underline"
                    >
                      [{link.type}] {link.title || link.url}
                      <ExternalLink className="h-3 w-3" strokeWidth={1.8} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2 border-t border-cream-border pt-3 text-[12px]">
            <button
              onClick={onPin}
              className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-2.5 py-1 text-taupe hover:text-navy-deep"
            >
              {entry.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
              {entry.pinned ? "Открепить" : "Закрепить"}
            </button>
            <button
              onClick={onArchive}
              className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-2.5 py-1 text-taupe hover:text-navy-deep"
            >
              {entry.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
              {entry.archived ? "Восстановить" : "Архив"}
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-2.5 py-1 text-coral-dark hover:bg-coral/10"
            >
              <Trash2 className="h-3 w-3" />
              Удалить
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Create modal
// ============================================================================

function CreateModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<KnowledgeBaseCreate>({
    category: "general",
    title: "",
    content: "",
    tags: [],
    external_links: [],
  });
  const [tagsRaw, setTagsRaw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setError(t("knowledge.title_required"));
      return;
    }
    setSaving(true);
    try {
      await createKnowledge({
        ...form,
        tags: tagsRaw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
          .slice(0, 8),
      });
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("knowledge.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-cream-light p-5 shadow-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <SectionLabel>{t("knowledge.modal_new_entry")}</SectionLabel>
        <h2 className="mt-1 font-serif text-[20px] font-medium">{t("knowledge.modal_manual")}</h2>

        <div className="mt-4 space-y-3 text-[14px]">
          <div>
            <label className="mb-1 block text-[12px] uppercase tracking-wider text-taupe">
              {t("knowledge.modal_category")}
            </label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value as KnowledgeCategory })
              }
              className="w-full rounded-md border border-cream-border bg-cream px-3 py-2"
            >
              {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                <option key={c.id} value={c.id}>
                  {t(c.label)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[12px] uppercase tracking-wider text-taupe">
              {t("knowledge.modal_title")}
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-md border border-cream-border bg-cream px-3 py-2"
              maxLength={200}
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] uppercase tracking-wider text-taupe">
              Content (Markdown)
            </label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={8}
              className="w-full rounded-md border border-cream-border bg-cream px-3 py-2 font-mono text-[13px]"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] uppercase tracking-wider text-taupe">
              {t("knowledge.modal_tags")}
            </label>
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              className="w-full rounded-md border border-cream-border bg-cream px-3 py-2"
              placeholder="shoes, neutral, long_run"
            />
          </div>
          {error ? <p className="text-[13px] text-coral-dark">{error}</p> : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("knowledge.modal_cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Сохраняю…" : "Сохранить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
